import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Search } from "lucide-react";
import { companyFormToastError } from "./companyFormSwal";
import { usePermissions } from "../../utilis/PermissionsContext";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import CompanyFormsPageHeader from "./CompanyFormsPageHeader";
import CompanyFormsEmptyState from "./CompanyFormsEmptyState";
import CompanyFormsStatsRow from "./CompanyFormsStatsRow";
import DocumentLibraryCard from "./DocumentLibraryCard";
import {
  CompanyFormsLibraryGridSkeleton,
  CompanyFormsLibraryPageSkeleton,
} from "./CompanyFormsSkeletons";
import { useCompanyFormCategories } from "./useCompanyFormCategories";
import { useCompanyFormDestinations } from "./useCompanyFormDestinations";
import Paginations from "../GenericComponents/Pagination";
import {
  COMPANY_FORMS_MODULE_CONTENT,
  COMPANY_FORMS_MODULE_PAGE_SHELL,
  COMPANY_FORMS_ROOT,
  LIBRARY_STAT_CARDS,
  MODULE_PAGE_SHELL_STYLE,
  canUploadCompanyForms,
  canApproveCompanyForms,
} from "./companyFormConstants";

const FILTER_SELECT_STYLE = {
  appearance: "none",
  WebkitAppearance: "none",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2394A3B8' d='M3 4.5 6 7.5 9 4.5'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  backgroundSize: "12px",
  paddingRight: "2.25rem",
};

const FILTER_SEARCH_INPUT_STYLE = {
  boxShadow: "none",
  width: "100%",
  minHeight: "40px",
  paddingTop: "9px",
  paddingBottom: "9px",
  paddingLeft: "2.75rem",
  paddingRight: "12px",
};

function LibraryFiltersBar({
  searchDraft,
  onSearchChange,
  categoryFilter,
  onCategoryChange,
  destinationFilter,
  onDestinationChange,
  destinations,
  onClear,
  hasActiveFilters,
  categories,
}) {
  return (
    <div className="mb-4 w-full rounded-xl border border-[#E5ECF5] bg-white px-3 py-3 shadow-[0_1px_2px_rgba(20,33,61,0.05)] sm:px-4">
      <div className="flex w-full flex-row flex-nowrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <span
            className="pointer-events-none absolute left-2.5 top-1/2 z-[1] inline-flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-md bg-[#EFF6FF] text-[#007BFF]"
            aria-hidden="true"
          >
            <Search size={13} strokeWidth={1.75} />
          </span>
          <input
            type="text"
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            value={searchDraft}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search documents…"
            className="box-border w-full rounded-lg border border-[#E5ECF5] bg-white text-sm text-[#14213D] shadow-none outline-none transition focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/15"
            style={FILTER_SEARCH_INPUT_STYLE}
          />
        </div>
        <select
          value={destinationFilter}
          onChange={(e) => onDestinationChange(e.target.value)}
          className="box-border w-[180px] max-w-[34%] shrink-0 min-h-[40px] cursor-pointer rounded-lg border border-[#E5ECF5] bg-white py-2 pl-3 text-sm text-[#475569] shadow-none outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/15"
          style={FILTER_SELECT_STYLE}
          title="Destination country"
        >
          <option value="">All destinations</option>
          {(destinations || []).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => onCategoryChange(e.target.value)}
          className="box-border w-[200px] max-w-[40%] shrink-0 min-h-[40px] cursor-pointer rounded-lg border border-[#E5ECF5] bg-white py-2 pl-3 text-sm text-[#475569] shadow-none outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/15"
          style={FILTER_SELECT_STYLE}
        >
          <option value="">All categories</option>
          {(categories || []).map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={onClear}
            className="company-forms-btn shrink-0 rounded-lg border border-[#E5ECF5] bg-white px-2.5 py-2 text-[11px] font-semibold text-[#64748B] transition hover:bg-[#F8FAFC]"
          >
            Clear
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function CompanyFormsLibrary() {
  const { categories: formCategories, categoryLabels } = useCompanyFormCategories();
  const { destinations } = useCompanyFormDestinations();
  const { permissions } = usePermissions();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [destinationFilter, setDestinationFilter] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 12,
    total: 0,
    lastPage: 1,
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchDraft);
      setPagination((p) => ({ ...p, currentPage: 1 }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchDraft]);

  const canUpload = canUploadCompanyForms(permissions);
  const canApprove = canApproveCompanyForms(permissions);

  const libraryStats = useMemo(
    () => ({
      total: pagination.total,
      categories: formCategories.length,
    }),
    [pagination.total, formCategories.length],
  );

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        perPage: pagination.perPage,
      };
      if (search.trim()) params.search = search.trim();
      if (categoryFilter) params.category = categoryFilter;
      if (destinationFilter) params.destination_country = destinationFilter;

      const response = await apiUrl.get("/company-forms/library", { params });
      setForms(response?.data?.data || []);

      const pageInfo = response?.data?.pagination;
      if (pageInfo) {
        setPagination((prev) => ({
          ...prev,
          total: pageInfo.total || 0,
          lastPage: pageInfo.lastPage || 1,
          currentPage: pageInfo.currentPage || prev.currentPage,
          perPage: pageInfo.perPage || prev.perPage,
        }));
      }
    } catch (error) {
      companyFormToastError(
        "Error",
        error.response?.data?.message || "Failed to load documents",
      );
      setForms([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.perPage, search, categoryFilter, destinationFilter]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  const perItem = Math.min(
    pagination.perPage,
    Math.max(0, pagination.total - (pagination.currentPage - 1) * pagination.perPage),
  );

  const hasActiveFilters = Boolean(
    searchDraft.trim() || categoryFilter || destinationFilter,
  );

  const clearFilters = () => {
    setSearchDraft("");
    setSearch("");
    setCategoryFilter("");
    setDestinationFilter("");
    setPagination((p) => ({ ...p, currentPage: 1 }));
  };

  const handlePaginationCallback = (page, perPage) => {
    if (perPage && perPage !== "") {
      setPagination((prev) => ({
        ...prev,
        perPage: parseInt(perPage, 10),
        currentPage: 1,
      }));
    } else {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
      }));
    }
  };

  const secondaryBtn =
    "company-forms-btn inline-flex items-center rounded-lg border border-[#BFDBFE] bg-[#EFF6FF] px-3.5 py-2 text-xs font-semibold text-[#007BFF] shadow-sm hover:bg-[#DBEAFE]/60";

  return (
    <div className={COMPANY_FORMS_ROOT}>
      <CompanyFormsPageHeader
        icon={BookOpen}
        title="View documents"
        subtitle="Browse approved documents available to your team."
        actions={
          <>
            {canUpload ? (
              <Link to="/company-forms/manage" className={secondaryBtn}>
                Upload documents
              </Link>
            ) : null}
            {canApprove ? (
              <Link
                to="/company-forms/approve"
                className="company-forms-btn inline-flex items-center rounded-lg bg-[#007BFF] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0069d9]"
              >
                Approve documents
              </Link>
            ) : null}
          </>
        }
      />

      <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
        <div className={COMPANY_FORMS_MODULE_CONTENT}>
          {loading && forms.length === 0 && pagination.total === 0 ? (
            <CompanyFormsLibraryPageSkeleton />
          ) : (
            <>
              <CompanyFormsStatsRow
                cards={LIBRARY_STAT_CARDS}
                stats={libraryStats}
                loading={loading && pagination.total === 0}
              />

              <LibraryFiltersBar
                searchDraft={searchDraft}
                onSearchChange={setSearchDraft}
                categoryFilter={categoryFilter}
                onCategoryChange={(value) => {
                  setCategoryFilter(value);
                  setPagination((p) => ({ ...p, currentPage: 1 }));
                }}
                destinationFilter={destinationFilter}
                onDestinationChange={(value) => {
                  setDestinationFilter(value);
                  setPagination((p) => ({ ...p, currentPage: 1 }));
                }}
                destinations={destinations}
                onClear={clearFilters}
                hasActiveFilters={hasActiveFilters}
                categories={formCategories}
              />

              {loading ? (
                <CompanyFormsLibraryGridSkeleton count={8} />
              ) : forms.length === 0 ? (
                <CompanyFormsEmptyState
                  icon={BookOpen}
                  title={
                    hasActiveFilters
                      ? "No documents match your filters"
                      : "No published documents yet"
                  }
                  description={
                    hasActiveFilters
                      ? "Try clearing filters or search with different terms."
                      : "Approved documents will appear here once uploads are published."
                  }
                >
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="company-forms-btn inline-flex rounded-lg bg-[#007BFF] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0069d9]"
                    >
                      Clear filters
                    </button>
                  ) : canUpload ? (
                    <Link
                      to="/company-forms/manage"
                      className="company-forms-btn inline-flex rounded-lg bg-[#007BFF] px-4 py-2 text-xs font-semibold text-white no-underline hover:bg-[#0069d9]"
                    >
                      Upload document
                    </Link>
                  ) : null}
                </CompanyFormsEmptyState>
              ) : (
                <div className="company-forms-library-grid">
                  {forms.map((form) => (
                    <DocumentLibraryCard
                      key={form.id}
                      form={form}
                      categoryLabels={categoryLabels}
                    />
                  ))}
                </div>
              )}

              {pagination.total > 0 ? (
                <div className="mt-4">
                  <Paginations
                    currentPage={pagination.currentPage}
                    itemsPerPage={pagination.perPage}
                    lastPage={pagination.lastPage}
                    onPageChange={handlePaginationCallback}
                    totalItems={pagination.total}
                    perItem={perItem}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
